import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency, yearOf } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import {
  LIMIT_PAUSAL, LIMIT_PDV, toRSD, topCustomers, formatRSD, formatPct, nextTaxDeadline,
} from '../lib/helpers';

const MONTHLY_TAX = 29428;

// Kratak prikaz iznosa na grafikonu: 31k, 1,2M
const compact = (n) => {
  if (n >= 1e6) return (n / 1e6).toLocaleString('sr-Latn-RS', { maximumFractionDigits: 1 }) + 'M';
  if (n >= 1000) return Math.round(n / 1000) + 'k';
  return n > 0 ? String(Math.round(n)) : '';
};

const pctText = (cur, max) =>
  ((cur / max) * 100).toLocaleString('sr-Latn-RS', { maximumFractionDigits: 1 }) + '%';


export default function Dashboard() {
  const { activeAgencyId, year, currentYear, profile } = useAgency();
  const { t, lang, tr } = useI18n();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taxPaid, setTaxPaid] = useState(false);
  const [taxSaving, setTaxSaving] = useState(false);

  const deadline0 = nextTaxDeadline();
  const taxPeriodYear = deadline0.getMonth() === 0 ? deadline0.getFullYear() - 1 : deadline0.getFullYear();
  const taxPeriodMonth = deadline0.getMonth() === 0 ? 12 : deadline0.getMonth();

  useEffect(() => {
    if (!activeAgencyId) return;
    (async () => {
      const [{ data: inv }, { data: taxRow }] = await Promise.all([
        supabase
          .from('invoices')
          .select('*, customer:customer_id(name)')
          .eq('agency_id', activeAgencyId)
          .order('issue_date', { ascending: false }),
        supabase
          .from('tax_payments')
          .select('*')
          .eq('agency_id', activeAgencyId)
          .eq('period_year', taxPeriodYear)
          .eq('period_month', taxPeriodMonth)
          .maybeSingle(),
      ]);
      setInvoices(inv || []);
      setTaxPaid(Boolean(taxRow?.paid));
      setLoading(false);
    })();
  }, [activeAgencyId, taxPeriodYear, taxPeriodMonth]);

  const markTaxPaid = async () => {
    setTaxSaving(true);
    const { error } = await supabase.from('tax_payments').upsert({
      agency_id: activeAgencyId,
      period_year: taxPeriodYear,
      period_month: taxPeriodMonth,
      paid: true,
      paid_at: new Date().toISOString(),
      amount: MONTHLY_TAX,
    }, { onConflict: 'agency_id,period_month,period_year' });
    if (!error) setTaxPaid(true);
    setTaxSaving(false);
  };

  // Sve se računa za godinu izabranu u levom meniju
  const yearInvoices = useMemo(
    () => invoices.filter((i) => yearOf(i.issue_date) === year && i.document_kind !== 'profaktura'),
    [invoices, year],
  );

  // Limit 6M (paušal) — svi dokumenti u kalendarskoj godini
  const pausalTotal = useMemo(() => yearInvoices.reduce((s, i) => s + toRSD(i), 0), [yearInvoices]);

  // Limit 8M (PDV) — domaći promet u poslednjih 365 dana; za raniju godinu računa se do 31.12. te godine
  const pdvTotal = useMemo(() => {
    const end = year === currentYear ? new Date() : new Date(year, 11, 31, 23, 59, 59);
    const cutoff = new Date(end);
    cutoff.setDate(cutoff.getDate() - 365);
    return invoices
      .filter((i) => (i.type === 'domaca' || i.document_kind === 'fiskalni') && i.document_kind !== 'profaktura' && i.status !== 'stornirano')
      .filter((i) => { const d = new Date(i.issue_date + 'T00:00:00'); return d >= cutoff && d <= end; })
      .reduce((s, i) => s + i.amount, 0);
  }, [invoices, year, currentYear]);

  // Grafikon: svih 12 meseci izabrane godine
  const chartData = useMemo(() => {
    const buckets = Array.from({ length: 12 }, (_, m) => ({ year, month: m, total: 0 }));
    yearInvoices.forEach((i) => {
      const m = Number(String(i.issue_date).slice(5, 7)) - 1;
      if (buckets[m]) buckets[m].total += toRSD(i);
    });
    return buckets;
  }, [yearInvoices, year]);

  const top4 = useMemo(() => topCustomers(yearInvoices, 4), [yearInvoices]);

  const deadline = nextTaxDeadline();
  const deadlineStr = deadline.toLocaleDateString(lang === 'en' ? 'en-GB' : 'sr-Latn-RS', { day: 'numeric', month: 'short' });
  const daysUntil = Math.ceil((deadline - new Date()) / 86400000);

  if (loading) return <div style={styles.loading}>{t('loadingGeneric')}</div>;

  const locale = lang === 'en' ? 'en-GB' : 'sr-Latn-RS';
  const todayStr = new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const monthNames = t('monthNames');
  const nowMonth = new Date().getMonth();
  const chartMax = Math.max(...chartData.map((b) => b.total), 1);
  const yearTotal = chartData.reduce((sum, b) => sum + b.total, 0);
  const isCurrentYear = year === currentYear;

  // Limit kao niz "kockica" (Verde stil)
  const TICKS = 34;
  const limitBlock = (label, current, max, color, last) => {
    const on = Math.round(Math.min(current / max, 1) * TICKS);
    return (
      <div style={{ marginBottom: last ? 0 : 18, position: 'relative' }}>
        <div style={styles.limitHead}>
          <span className="mono-label">{label}</span>
          <span className="mono" style={styles.limitPct}>{pctText(current, max)}</span>
        </div>
        <div className="mono" style={styles.limitValue}>
          {formatRSD(current).replace(' RSD', '')} <span style={styles.limitMax}>/ {formatRSD(max).replace(',00', '').replace(' RSD', '')}</span>
        </div>
        <div style={styles.ticks} aria-hidden="true">
          {Array.from({ length: TICKS }, (_, i) => (
            <span key={i} style={{ ...styles.tick, background: i < on ? color : 'var(--line)' }} />
          ))}
        </div>
        <div style={styles.limitNote} className="mono">{t('limitRemaining', formatRSD(Math.max(max - current, 0)), pctText(current, max))}</div>
      </div>
    );
  };

  // ---------- linijski grafikon (meseci) ----------
  const lastMonth = isCurrentYear ? nowMonth : 11;
  const CW = 600, CH = 150, PAD = 6;
  const pts = chartData.slice(0, lastMonth + 1).map((b, i) => [
    PAD + (i * (CW - 2 * PAD)) / 11,
    CH - PAD - (b.total / chartMax) * (CH - 2 * PAD - 8),
  ]);
  const smooth = (p) => {
    if (!p.length) return '';
    let d = `M${p[0][0].toFixed(1)},${p[0][1].toFixed(1)}`;
    for (let i = 0; i < p.length - 1; i++) {
      const p0 = p[i - 1] || p[i]; const p1 = p[i]; const p2 = p[i + 1]; const p3 = p[i + 2] || p2;
      const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
      const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
      d += ` C${c1[0].toFixed(1)},${c1[1].toFixed(1)} ${c2[0].toFixed(1)},${c2[1].toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
    }
    return d;
  };
  const linePath = smooth(pts);
  const areaPath = pts.length > 1 ? `${linePath} L${pts[pts.length - 1][0].toFixed(1)},${CH} L${pts[0][0].toFixed(1)},${CH} Z` : '';
  const lastPt = pts[pts.length - 1];
  // Poredi poslednji ZAVRŠEN mesec sa mesecom pre njega (tekući mesec tek počinje, pa bi uvek "padao")
  const compMonth = isCurrentYear ? nowMonth - 1 : 11;
  const curTotal = compMonth >= 0 ? chartData[compMonth].total : 0;
  const prevTotal = compMonth > 0 ? chartData[compMonth - 1].total : 0;
  const change = prevTotal > 0 ? ((curTotal - prevTotal) / prevTotal) * 100 : null;
  const compLabel = compMonth >= 0 && Array.isArray(monthNames) ? String(monthNames[compMonth]).slice(0, 3).toUpperCase() : '';

  // ---------- krug: najveći klijenti ----------
  const DONUT_COLORS = ['var(--teal-800)', 'var(--lavender-700)', 'var(--lavender-500)', 'var(--lavender-300)'];
  const topShare = top4.reduce((s, c) => s + c.pct, 0);
  const slices = [...top4.map((c, i) => ({ pct: c.pct, color: DONUT_COLORS[i] })), ...(topShare < 100 ? [{ pct: 100 - topShare, color: 'var(--line)' }] : [])];
  const R = 58, C0 = 2 * Math.PI * R;
  let acc = 0;

  return (
    <div style={styles.wrap}>
      {/* Naslov i brze radnje */}
      <div style={styles.headRow}>
        <div style={{ minWidth: 0 }}>
          <div className="mono-label" style={styles.helloDate}>{todayStr.charAt(0).toUpperCase() + todayStr.slice(1)}</div>
          <h1 style={styles.heading}>{tr('Pregled poslovanja', 'Business overview', 'Обзор')}</h1>
        </div>
        <div style={styles.actions} className="dash-actions">
          <button style={styles.secondaryAction} onClick={() => navigate('/klijenti/novi')}>
            <Icon name="userPlus" size={15} />{t('newClient')}
          </button>
          <button style={styles.secondaryAction} onClick={() => navigate('/fakturisanje/nova-profaktura')}>
            <Icon name="fileText" size={15} />{t('newProforma')}
          </button>
          <button style={styles.primaryAction} onClick={() => navigate('/fakturisanje/nova')}>
            <Icon name="plus" size={15} strokeWidth={2.2} />{t('newInvoice')}
          </button>
        </div>
      </div>

      {/* Nova firma (još nema nijedan dokument): kartica "Prvi koraci" */}
      {invoices.length === 0 && <FirstSteps agencyId={activeAgencyId} profile={profile} navigate={navigate} tr={tr} />}

      <div style={styles.grid} className="dash-grid">
        {/* Fakturisano po mesecima — linija sa lavanda senkom */}
        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitleRow}>
              <span style={styles.cardIcon}><Icon name="chart" size={15} color="var(--lavender-700)" strokeWidth={1.9} /></span>
              <h2 style={styles.cardTitle}>{t('invoicedTitle')}</h2>
            </div>
            <span style={styles.cardTag} className="mono">{year}</span>
          </div>
          <div style={styles.bigRow}>
            <span className="mono" style={styles.bigTotal}>{formatRSD(yearTotal).replace(' RSD', '')}</span>
            {change !== null && (
              <span className="mono" style={{ ...styles.change, color: change >= 0 ? 'var(--mint-700)' : 'var(--gold-600)' }}>
                {change >= 0 ? '↑' : '↓'} {Math.abs(change).toLocaleString('sr-Latn-RS', { maximumFractionDigits: 1 })} % {compLabel}
              </span>
            )}
            <span className="mono-label" style={{ marginLeft: 'auto' }}>{t('yearTotalShort', year)} · RSD</span>
          </div>
          <div className="dash-chart" style={{ position: 'relative', height: 160, marginTop: 4 }}>
          <svg viewBox={`0 0 ${CW} ${CH + 2}`} width="100%" height="100%" preserveAspectRatio="none" aria-label={t('invoicedTitle')} style={{ display: 'block', maxWidth: '100%' }}>
            <defs>
              <linearGradient id="dashFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9d90e3" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#9d90e3" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[20, 60, 100, 140].map((y) => <line key={y} x1="0" x2={CW} y1={y} y2={y} stroke="var(--line)" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
            {areaPath && <path d={areaPath} fill="url(#dashFill)" />}
            {linePath && <path d={linePath} fill="none" stroke="var(--lavender-700)" strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
          </svg>
          {lastPt && yearTotal > 0 && (
            <span style={{ ...styles.lastDot, left: `${(lastPt[0] / CW) * 100}%`, top: `calc(${(lastPt[1] / (CH + 2)) * 100}% - 5px)` }} />
          )}
          </div>
          <div style={styles.months} className="mono">
            {chartData.map((b) => {
              const label = Array.isArray(monthNames) ? String(monthNames[b.month]).slice(0, 3).toUpperCase() : b.month + 1;
              const current = isCurrentYear && b.month === nowMonth;
              return <span key={b.month} title={`${label}: ${formatRSD(b.total)}`} style={{ flex: 1, textAlign: 'center', ...(current ? { color: 'var(--ink-900)', fontWeight: 600 } : {}) }}>{label}</span>;
            })}
          </div>
        </section>

        {/* Limiti — glavna kartica sa prelazom */}
        <section style={styles.card} className="glow-card">
          <div style={styles.cardHeader}>
            <div style={styles.cardTitleRow}>
              <span style={styles.cardIcon}><Icon name="chart" size={15} color="var(--lavender-700)" strokeWidth={1.9} /></span>
              <h2 style={styles.cardTitle}>{t('limits')}</h2>
            </div>
            <span style={styles.cardTag} className="mono">{year}</span>
          </div>
          {limitBlock(t('invoicedYear'), pausalTotal, LIMIT_PAUSAL, 'var(--lavender-700)')}
          {limitBlock(t('collected365'), pdvTotal, LIMIT_PDV, 'var(--teal-800)', true)}
        </section>

        {/* Najveći klijenti — krug */}
        <section style={styles.card}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitleRow}>
              <span style={styles.cardIcon}><Icon name="users" size={15} color="var(--lavender-700)" strokeWidth={1.9} /></span>
              <h2 style={styles.cardTitle}>{t('topClients')}</h2>
            </div>
            <button onClick={() => navigate('/klijenti')} style={styles.linkBtn} className="mono">{t('allClientsArrow')}</button>
          </div>
          {top4.length === 0 ? (
            <p style={styles.emptyNote}>{t('noInvoicedYet')}</p>
          ) : (
            <div style={styles.donutRow} className="dash-donut">
              <div style={styles.donutWrap}>
                <span style={styles.donutGlow} />
                <svg width="160" height="160" viewBox="0 0 160 160" style={{ position: 'relative' }} aria-hidden="true">
                  {slices.map((sl, i) => {
                    const len = (C0 * sl.pct) / 100;
                    const el = (
                      <circle key={i} cx="80" cy="80" r={R} fill="none" stroke={sl.color} strokeWidth="15"
                        strokeDasharray={`${Math.max(len - 2, 0)} ${C0 - Math.max(len - 2, 0)}`} strokeDashoffset={-acc} transform="rotate(-90 80 80)" />
                    );
                    acc += len;
                    return el;
                  })}
                </svg>
                <span style={styles.donutCenter}>
                  <span style={styles.donutCenterIn}>
                    <span style={{ fontSize: 10, color: 'var(--ink-500)' }}>{tr('Ukupno', 'Total', 'Всего')}</span>
                    <span className="mono" style={{ fontSize: 14, fontWeight: 600 }}>{compact(yearTotal) || '0'}</span>
                  </span>
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {top4.map((c, i) => (
                  <div key={c.name} style={styles.clientRow}>
                    <span style={{ ...styles.legendDot, background: DONUT_COLORS[i] }} />
                    <span style={styles.clientName}>{c.name}</span>
                    <span className="mono-label" style={styles.clientTag}>{c.isForeign ? t('foreignEntity') : t('domesticEntity')}</span>
                    <span className="mono" style={styles.clientAmount}>{formatRSD(c.total).replace(' RSD', '')}</span>
                    <span className="mono" style={styles.clientPct}>{formatPct(c.pct)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Porezi i doprinosi */}
        <section style={{ ...styles.card, display: 'flex', flexDirection: 'column' }}>
          <div style={styles.cardHeader}>
            <div style={styles.cardTitleRow}>
              <span style={styles.cardIcon}><Icon name="percent" size={15} color="var(--lavender-700)" strokeWidth={1.9} /></span>
              <h2 style={styles.cardTitle}>{t('taxesCard')}</h2>
            </div>
            {taxPaid ? (
              <span style={{ ...styles.cardTag, color: 'var(--mint-700)' }} className="mono">{t('paidShort')}</span>
            ) : (
              <span style={{ ...styles.cardTag, color: 'var(--gold-600)' }} className="mono">
                {daysUntil > 0 ? t('daysLeft', daysUntil) : t('deadlinePassed')}
              </span>
            )}
          </div>
          <div style={styles.taxGrid}>
            <div style={styles.taxBox}>
              <div className="mono-label">{t('nextDeadline')}</div>
              <div style={styles.bigNumber} className="mono">{deadlineStr}</div>
              <div style={styles.taxSub}>{Array.isArray(monthNames) ? monthNames[taxPeriodMonth - 1] : ''} {taxPeriodYear}.</div>
            </div>
            <div style={styles.taxBox}>
              <div className="mono-label">{t('monthlyDue')}</div>
              <div style={styles.bigNumber} className="mono">{formatRSD(MONTHLY_TAX).replace(' RSD', '')}</div>
              <div style={styles.taxSub}>RSD</div>
            </div>
          </div>
          <div style={{ flex: 1 }} />
          {!taxPaid ? (
            <button style={styles.markPaidBtn} onClick={markTaxPaid} disabled={taxSaving}>
              <Icon name="check" size={15} strokeWidth={2.2} />
              {taxSaving ? t('savingEllipsis') : t('markPaid')}
            </button>
          ) : (
            <div style={styles.daysNote}>{daysUntil > 0 ? t('nextPaymentIn', daysUntil) : t('deadlinePassed')}</div>
          )}
          <button onClick={() => navigate('/poreski-kalendar')} style={{ ...styles.linkBtn, alignSelf: 'center', marginTop: 10 }} className="mono">
            {tr('Svi rokovi ↗', 'All deadlines ↗', 'Все сроки ↗')}
          </button>
        </section>
      </div>

      <style>{`
        @media (max-width: 1100px) { .dash-grid { grid-template-columns: 1fr !important; } }
        .dash-grid > section { min-width: 0; }
        @media (max-width: 760px) {
          .dash-chart { height: 120px !important; }
          .dash-actions { width: 100%; }
          .dash-actions > button { flex: 1 1 0; justify-content: center; }
          .dash-actions > button:last-child { order: -1; flex-basis: 100%; }
          .dash-donut { flex-direction: column; align-items: stretch !important; }
          .dash-donut > div:first-child { align-self: center; }
        }
      `}</style>
    </div>
  );
}

// ---------- Prvi koraci (nestaje kad se napravi prvi dokument ili kad se sakrije) ----------
const lsGet = (k) => { try { return localStorage.getItem(k) === '1'; } catch { return false; } };
const lsSet = (k) => { try { localStorage.setItem(k, '1'); } catch { /* ignore */ } };

function FirstSteps({ agencyId, profile, navigate, tr }) {
  const [clients, setClients] = useState(null);
  const logoKey = `finart_logo_skip_${agencyId}`;
  const hideKey = `finart_steps_hidden_${agencyId}`;
  const [logoSkipped, setLogoSkipped] = useState(() => lsGet(logoKey));
  const [hidden, setHidden] = useState(() => lsGet(hideKey));

  useEffect(() => {
    let cancelled = false;
    supabase.from('customers').select('id', { count: 'exact', head: true }).eq('agency_id', agencyId)
      .then(({ count }) => { if (!cancelled) setClients(count || 0); });
    return () => { cancelled = true; };
  }, [agencyId]);

  if (hidden) return null;

  const steps = [
    { key: 'firm', label: tr('Podaci firme', 'Company details', 'Данные фирмы'), done: Boolean(profile?.company_name && profile?.pib), to: '/agencija' },
    { key: 'logo', label: tr('Logo', 'Logo', 'Логотип'), optional: true, done: Boolean(profile?.logo_data), skipped: logoSkipped && !profile?.logo_data, to: '/agencija' },
    { key: 'client', label: tr('Prvi klijent', 'First client', 'Первый клиент'), done: clients > 0, to: '/klijenti/novi' },
    { key: 'invoice', label: tr('Prva faktura', 'First invoice', 'Первый счёт'), done: false, to: '/fakturisanje/nova' },
  ];
  const doneCount = steps.filter((s) => s.done || s.skipped).length;

  const skipLogo = () => { lsSet(logoKey); setLogoSkipped(true); };
  const hide = () => { lsSet(hideKey); setHidden(true); };

  return (
    <section style={fs.card}>
      <div style={fs.head}>
        <span style={fs.title}>{tr('Prvi koraci', 'First steps', 'Первые шаги')}</span>
        <span style={fs.headRight}>
          <span style={fs.count}>{tr(`${doneCount} od 4`, `${doneCount} of 4`, `${doneCount} из 4`)}</span>
          <button type="button" onClick={hide} style={fs.hideBtn}>{tr('Sakrij', 'Hide', 'Скрыть')}</button>
        </span>
      </div>
      <div style={fs.track}><div style={{ ...fs.fill, width: `${(doneCount / 4) * 100}%` }} /></div>
      {steps.map((s, i) => {
        const finished = s.done || s.skipped;
        return (
          <div key={s.key} style={fs.row}>
            <button type="button" onClick={() => navigate(s.to)} style={fs.rowMain}>
              <span style={{ ...fs.mark, ...(s.done ? fs.markDone : {}) }}>{s.done ? '✓' : s.skipped ? '–' : i + 1}</span>
              <span style={{ ...fs.label, ...(finished ? fs.labelDone : {}) }}>
                {s.label}
                {s.optional && !finished && <span style={fs.optional}> · {tr('nije obavezno', 'optional', 'необязательно')}</span>}
                {s.skipped && <span style={fs.optional}> · {tr('preskočeno', 'skipped', 'пропущено')}</span>}
              </span>
              {!finished && <span style={fs.go}>{tr('Kreni →', 'Go →', 'Далее →')}</span>}
            </button>
            {s.optional && !finished && (
              <button type="button" onClick={skipLogo} style={fs.skipBtn}>{tr('Preskoči', 'Skip', 'Пропустить')}</button>
            )}
          </div>
        );
      })}
    </section>
  );
}

const fs = {
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: 18, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 },
  headRight: { display: 'flex', alignItems: 'center', gap: 12 },
  title: { fontSize: 17, fontWeight: 800, color: 'var(--ink-900)' },
  count: { fontSize: 13, fontWeight: 700, color: 'var(--teal-800)' },
  hideBtn: { border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  track: { height: 6, borderRadius: 99, background: 'var(--line)', overflow: 'hidden' },
  fill: { height: '100%', background: 'var(--teal-800)', borderRadius: 99, transition: 'width 0.3s ease' },
  row: { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--paper)', borderRadius: 8, paddingRight: 6 },
  rowMain: { flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, minHeight: 46, padding: '0 12px', border: 'none', background: 'transparent', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit' },
  mark: { flexShrink: 0, width: 24, height: 24, borderRadius: '50%', background: 'var(--line)', color: 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800 },
  markDone: { background: 'var(--paper)', color: 'var(--forest-600)' },
  label: { flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--ink-900)' },
  labelDone: { color: 'var(--ink-500)' },
  optional: { fontWeight: 500, color: 'var(--ink-500)' },
  go: { fontSize: 13, fontWeight: 700, color: 'var(--teal-800)', whiteSpace: 'nowrap' },
  skipBtn: { flexShrink: 0, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', borderRadius: 9, padding: '6px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
};

const styles = {
  loading: { padding: 40, textAlign: 'center', color: 'var(--ink-500)' },
  wrap: { display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1180, margin: '0 auto' },
  headRow: { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' },
  helloDate: { marginBottom: 4 },
  heading: { fontSize: 'var(--fs-page, 22px)', fontWeight: 700, letterSpacing: '-0.02em', margin: 0 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  primaryAction: { display: 'flex', alignItems: 'center', gap: 7, minHeight: 34, padding: '0 14px', border: 'none', borderRadius: 8, background: 'var(--teal-800)', color: '#fff', fontSize: 'var(--fs-table, 13px)', fontWeight: 600 },
  secondaryAction: { display: 'flex', alignItems: 'center', gap: 7, minHeight: 34, padding: '0 12px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-900)', fontSize: 'var(--fs-table, 13px)', fontWeight: 500 },
  grid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)', gap: 14 },
  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 14, padding: '16px 18px', minWidth: 0 },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14, position: 'relative' },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: 10 },
  cardIcon: { width: 28, height: 28, borderRadius: 8, background: 'var(--lavender-100)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardTitle: { fontSize: 14, fontWeight: 600, margin: 0 },
  cardTag: { fontSize: 'var(--fs-micro, 11px)', letterSpacing: '0.06em', color: 'var(--ink-500)', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: 6, padding: '2px 8px' },
  bigRow: { display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' },
  bigTotal: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' },
  change: { fontSize: 12, fontWeight: 600 },
  lastDot: { position: 'absolute', width: 10, height: 10, marginLeft: -5, borderRadius: '50%', background: '#fff', border: '2px solid var(--lavender-700)' },
  months: { display: 'flex', fontSize: 10, letterSpacing: '0.06em', color: 'var(--ink-500)', marginTop: 4 },
  limitHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  limitPct: { fontSize: 11, color: 'var(--ink-500)' },
  limitValue: { fontSize: 19, fontWeight: 600, margin: '6px 0 8px' },
  limitMax: { fontSize: 11, fontWeight: 400, color: 'var(--ink-500)' },
  ticks: { display: 'flex', gap: 3 },
  tick: { flex: 1, height: 12, borderRadius: 2 },
  limitNote: { fontSize: 11, color: 'var(--ink-500)', marginTop: 8 },
  donutRow: { display: 'flex', alignItems: 'center', gap: 22 },
  donutWrap: { position: 'relative', width: 160, height: 160, flexShrink: 0 },
  donutGlow: { position: 'absolute', inset: -16, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,205,247,0.8) 0%, rgba(157,144,227,0.32) 45%, rgba(18,52,63,0.05) 62%, rgba(255,255,255,0) 72%)', filter: 'blur(8px)' },
  donutCenter: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  donutCenterIn: { width: 78, height: 78, borderRadius: '50%', background: '#fff', boxShadow: '0 2px 10px rgba(18,52,63,0.08)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' },
  clientRow: { display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--line)', fontSize: 'var(--fs-table, 13px)' },
  legendDot: { width: 8, height: 8, borderRadius: 2, flexShrink: 0 },
  clientName: { flex: 1, minWidth: 0, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  clientTag: { fontSize: 10, border: '1px solid var(--line)', borderRadius: 5, padding: '1px 6px', whiteSpace: 'nowrap' },
  clientAmount: { width: 104, textAlign: 'right', fontWeight: 500 },
  clientPct: { width: 40, textAlign: 'right', color: 'var(--ink-500)' },
  emptyNote: { color: 'var(--ink-500)', fontSize: 'var(--fs-table, 13px)', margin: 0 },
  linkBtn: { border: 'none', background: 'none', color: 'var(--teal-800)', fontSize: 11, letterSpacing: '0.06em', fontWeight: 600, padding: 0, cursor: 'pointer' },
  taxGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  taxBox: { border: '1px solid var(--line)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 5 },
  bigNumber: { fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--ink-900)', lineHeight: 1.2 },
  taxSub: { fontSize: 12, color: 'var(--ink-500)' },
  markPaidBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 34, marginTop: 14, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-900)', fontSize: 'var(--fs-table, 13px)', fontWeight: 600, cursor: 'pointer' },
  daysNote: { marginTop: 14, fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-500)', textAlign: 'center' },
};
